// All user-visible natural language goes here

import { MenuItemName } from 'src/app/shared/page/menu.config';
import { SocialIcon } from '../../../core/footer/footer.types';
import { KeyUsages } from '../../entities/key-usages';
import { ExtendedKeyUsages } from '../../entities/extended-key-usages';
import { countries } from './countries';
import { usaStates } from './us-states';

import { CryptoTokenField } from 'src/app/features/cryptotokenlist/components/crypto-token-form/crypto-token-form.types';
import { CryptoTokenTypeList } from '../../entities/crypto-token-type';
import { CryptoToken } from '../../entities/crypto-token';
import {
  CaFormField,
  ValidityUnit,
} from 'src/app/features/calist/components/create-ca/create-ca.types';
import { ProfileFormField } from 'src/app/features/profilelist/components/profile-form/profile-form.types';
import { ProfileType } from '../../entities/profile-type';
import { Profile } from '../../entities/profile';
import { CryptotokenKey } from '../../entities/cryptotoken-key';
import { CA } from '../../entities/ca';
import { YesNo } from '../../util/yes-no';
import {
  ExpirationStatus,
  NearExpirationCategory,
} from 'src/app/features/calist/calist.types';
import { KeySpecificationList } from '../../entities/key-specification-type';
import { CreateCryptoTokenKeyField } from 'src/app/features/cryptotokenlist/components/create-crypto-token-key/create-crypto-token-key.types';
import {
  CaFullFormField,
  CaType,
} from 'src/app/features/operational-list/components/ca-full-form/ca-full-form.types';
import { CaRevokeReason } from '../../entities/ca-revoke-reason';

const SHORT_MONTHS_EN = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'June',
  'July',
  'Aug',
  'Sept',
  'Oct',
  'Nov',
  'Dec',
];

const profileTypeEnUs = {
  [ProfileType.END_ENTITY]: 'End Entity',
  [ProfileType.SUB_CA]: 'Sub CA',
  [ProfileType.ROOT_CA]: 'Root CA',
};

const caRevokeReasonEnUs: Record<CaRevokeReason, string> = {
  [CaRevokeReason.UNSPECIFIED]: 'Unspecified',
  [CaRevokeReason.KEYCOMPROMISE]: 'Key compromise',
  [CaRevokeReason.CACOMPROMISE]: 'CA compromise',
  [CaRevokeReason.AFFILIATIONCHANGED]: 'Affiliation changed',
  [CaRevokeReason.SUPERSEDED]: 'Superseded',
  [CaRevokeReason.CESSATIONOFOPERATION]: 'Cessation of operation',
  [CaRevokeReason.CERTIFICATEHOLD]: 'Certificate hold',
  [CaRevokeReason.REMOVEFROMCRL]: 'Remove from crl',
  [CaRevokeReason.PRIVILEGEWITHDRAWN]: 'Privilege withdrawn',
  [CaRevokeReason.AACOMPROMISE]: 'AA compromise',
};

export const LANG = {
  'en-US': {
    core: {
      sidebarMenu: {
        [MenuItemName.calist]: 'Certification Authorities',
        [MenuItemName.foo]: 'Foo',
        [MenuItemName.profilelist]: 'Profile List',
        [MenuItemName.cryptotokenlist]: 'Crypto Token List',
        [MenuItemName.provisionOperationalCa]: 'Provision Operational CA',
      },
      header: {
        services: 'Services',
        logOut: 'Log out',
        logIn: 'Login',
      },
      footer: {
        termsOfService: 'Terms of service',
        comcast: 'Comcast',
        socialIconsTooltips: {
          [SocialIcon.email]: 'Email to xPKI_Support@comcast.com',
          [SocialIcon.feedbackOrFeatureRequest]: 'Feedback or Feature request',
          [SocialIcon.slack]: 'Slack to #xpki-support',
        },
      },
      login: {
        title: 'What is XCA?',
        list: [
          'Xfinity Certificate Authority Administration',
          'This is the administration interface for Xfinity Certificate Authority system. It allows you to manage the PKI infrastructure, including creating new profiles and managing existing ones.',
        ],
        button: 'Login with SSO',
        passport: {
          inputs: {
            passport: {
              label: 'Passport',
              placeholder: 'Choose passport',
              errors: {
                required: 'Passport is required',
              },
            },
          },
          buttonText: 'Login with Passport',
        },
      },
    },
    features: {
      calist: {
        title: 'Certification Authorities',
        createButton: 'Create',
        headers: {
          caName: 'Name',
          expiry: 'Expiry',
          status: 'Status',
        },
        status: {
          [ExpirationStatus.Unknown]: 'Unknown',
          [ExpirationStatus.Active]: 'Active',
          [ExpirationStatus.Expired]: 'Expired',
        },
        nearExpirationCategory: {
          [NearExpirationCategory.expired]: 'Expired',
          [NearExpirationCategory.sixMonths]: '6 Months',
          [NearExpirationCategory.oneYear]: '1 Year',
          [NearExpirationCategory.overOneYear]: '> 1 Year',
        },
        details: {
          title: (caName: string) => `CA Details ${caName}`,
          na: 'n/a',
          back: 'Back to Certification Authorities',
          certificateChain: 'Certificate Chain',
          actions: {
            renew: {
              label: 'Renew',
              successToast: (caName: string) =>
                `CA ${caName} successfully renewed`,
              defaultFailToast: (caName: string) =>
                `Could not renew CA ${caName}`,
              dialogText: (caName: string) =>
                `Do you want to renew CA ${caName}?`,
              dialogTitle: 'Confirm renewal',
            },
            revoke: {
              label: 'Revoke',
              successToast: (caName: string) =>
                `CA ${caName} successfully revoked`,
              defaultFailToast: (caName: string) =>
                `Could not revoke CA ${caName}`,
            },
            generateCrl: {
              label: 'Generate CRL',
              defaultFailToast: (caName: string) =>
                `Could not generate CRL for CA ${caName}`,
              dialog: {
                title: (caName: string) => `CRL for CA ${caName}`,
                ok: 'OK',
              },
            },
          },
          data: {
            caName: 'CA Name',
            token: 'Token',
            signKey: 'Sign Key',
            signingAlgo: 'Signing Algo',
            defaultCRLDistPoint: 'Default CRL Dist Point',
            defaultOCSPServiceLocator: 'Default OCSP Service Locator',
            useLdapDNOrder: 'Use LDAP DN Order',
            subjectDn: 'Subject DN',
            expireTime: 'Expire Time',
            signedBy: 'Signed By',
            profile: 'Profile',
          },
        },
        revokeDialog: {
          title: 'Revoke',
          subtitle: (caName: string) => caName,
          inputs: {
            reason: {
              label: 'Reason',
              placeholder: 'Select reason',
              options: Object.entries(caRevokeReasonEnUs).map(
                ([value, label]) => ({ value, label }),
              ),
              errors: {
                required: 'Please specify the reason',
              },
            },
            revokeLeafCertificates: {
              label: 'Revoke Leaf Certificates',
            },
          },
          buttons: {
            revoke: 'Revoke',
            cancel: 'Cancel',
          },
        },

        toasts: {
          fetchingError: 'Error fetching Certificate Authorities',
          defaultDeletionError: (ca: CA) =>
            `Error deleting Certificate Authority ${ca.caName}`,
          deletionSuccess: (ca: CA) =>
            `Certificate Authority ${ca.caName} deleted successfully`,
          defaultCsrError: 'Could not create CSR',
          defaultImportError: 'Could not import cert chain',
          importSuccess: 'Cert chain successfully imported',
        },
        actions: {
          edit: {
            tooltip: 'Edit CA',
            confirmation: 'Do you want to discard your changes?',
            savingError: 'Could not save',
          },
          delete: {
            tooltip: 'Delete CA',
            confirmation: (caName: string) =>
              `Do you want to delete the CA ${caName}?`,
          },
        },
        edit: {
          title: (caName: string) => `Edit CA ${caName}`,
          buttons: {
            save: 'Save',
            cancel: 'Cancel',
          },
          confirmCancel: 'Do you want to discard your changes?',
        },
        create: {
          title: 'Create Certification Authority',
          editTitle: (caName: string) => `Edit CA ${caName}`,
          addOrgUnit: 'Add Org Unit',
          addSubjectOids: 'Add Subject OIDS',
          removeOrgUnit: 'Remove Org Unit',
          confirmCancel: 'Do you want to discard your changes?',
          orgUnits: {
            title: 'Organizational Units',
            noOrgUnits: 'No organizational Units',
          },
          selfSignedOption: 'Self-Signed',
          externallySignedOption: 'External',
          disabledProfileTooltip:
            'Profile is not needed for externally signed CA',
          csrHeader: 'CSR',
          inputs: {
            [CaFormField.subjectDn]: {
              label: 'Subject DN',
              placeholder: 'Enter DN',
              errors: {
                required: 'Subject DN is required',
              },
            },
            [CaFormField.profileName]: {
              label: 'Profile Name',
              placeholder: 'Enter Profile Name',
              errors: {
                required:
                  'Profile Name is required for non-externally signed CA',
                invalidCompletion: 'Invalid Profile Name',
              },
            },
            [CaFormField.tokenName]: {
              label: 'Token Name',
              placeholder: 'Enter Token Name',
              errors: {
                required: () => 'Token Name is required',
                invalidCompletion: 'Invalid Token Name',
              },
            },
            [CaFormField.signKey]: {
              label: 'Sign Key',
              placeholder: 'Enter Sign Key',
              errors: {
                required: () => 'Sign Key is required',
                invalidCompletion: 'Invalid Sign Key',
              },
            },
            [CaFormField.signingAlgo]: {
              label: 'Signing Algorithm',
              placeholder: 'Choose Signing Algorithm',
              errors: {
                required: () => 'Signing Algorithm is required',
              },
            },
            [CaFormField.signedBy]: {
              label: 'Signed By',
              placeholder: 'Enter Signed By',
              errors: {
                required: 'Signed By is required',
                invalidCompletion: 'Invalid CA',
              },
            },
            [CaFormField.validityUnits]: {
              label: 'Validity Units',
              placeholder: 'Choose Validity Units',
              options: [
                {
                  value: ValidityUnit.day,
                  label: 'Days',
                },
                {
                  value: ValidityUnit.year,
                  label: 'Years',
                },
              ],
            },
            [CaFormField.validityDays]: {
              label: 'Validity Days',
              placeholder: 'Enter Validity Days',
            },
            [CaFormField.validityYears]: {
              label: 'Validity Years',
              placeholder: 'Enter Validity Years',
            },
            [CaFormField.expirationDate]: {
              label: 'Expiration Date',
              placeholder: 'Enter expiration date',
              errors: {
                owlDateTimeMin: ({ min }: { min: Date }) =>
                  `Please choose a date not before ${min.toLocaleDateString()}`,
                owlDateTimeMax: ({ max }: { max: Date }) =>
                  `Please choose a date not after ${max.toLocaleDateString()}`,
                required: 'Expiration date is required',
              },
            },
            [CaFormField.defaultCRLDistPoint]: {
              label: 'Default CRL Dist Point',
              placeholder: 'Enter Default CRL Dist Point',
              errors: {
                pattern: 'The value must be a valid URL',
              },
            },
            [CaFormField.defaultOCSPServiceLocator]: {
              label: 'Default OCSP Service Locator',
              placeholder: 'Enter Default OCSP Service Locator',
            },
            [CaFormField.useLdapDnOrder]: {
              label: 'Use LDAP DN Order',
            },
            [CaFormField.certChain]: {
              label: 'Cert Chain',
            },
          },
          buttons: {
            create: 'Create',
            update: 'Update',
            cancel: 'Cancel',
            createCsr: 'Create CSR',
            importCertChain: 'Import Cert Chain',
          },
          toasts: {
            created: 'Successfully created CA',
            fail: 'Failed to create CA',
            couldNotFetchCryptoTokens: 'Could not fetch crypto tokens',
            couldNotFetchCryptoTokenKeys: 'Could not fetch crypto token keys',
            couldNotFetchProfiles: 'Could not fetch profiles',
            couldNotFetchCAs: 'Could not fetch certification authorities',
          },
        },
      },
      profilelist: {
        title: 'Profile List',
        createButton: 'Create',
        headers: {
          profileName: 'Name',
          profileType: 'Type',
        },
        actions: {
          delete: {
            tooltip: 'Delete Profile',
            confirmation: (profileName: string) =>
              `Do you want to delete the profile ${profileName}?`,
          },
          edit: {
            tooltip: 'Edit Profile',
          },
          clone: {
            tooltip: 'Clone Profile',
          },
        },
        toasts: {
          fetchingError: 'Error fetching Profile List',
          deletionError: 'Error deleting Profile',
          updateError: 'Could not update Profile',
          cloneError: 'Could not clone Profile',
        },
      },
      profiles: {
        title: (profileName: string) => `Profile ${profileName}`,
        na: 'n/a',
        back: 'Back to Profiles',
        critical: 'Critical',
        profileKeys: {
          profileName: 'Profile Name',
          maxCertificateValidityInDays: 'Max Certificate Validity In Days',
          allowExtensionOverride: 'Allow Extension Override',
          overridableExtensionOIDs: 'Overridable Extension OIDs',
          criticalBasicConstraints: 'Critical Basic Constraints',
          criticalKeyUsage: 'Critical Key Usage',
          keyUsages: 'Key Usages',
          criticalExtendedKeyUsage: 'Critical Extended Key Usage',
          extendedKeyUsages: 'Extended Key Usages',
          crlDistributionEndpoint: 'CRL Distribution Endpoint',
          useLastToFirstDnOrder: 'Use Last To First DN Order',
          ca: 'CA',
          caIssuerURI: 'CA Issuer URI',
          pathLengthConstraint: 'Path Length Constraint',
          profileType: 'Profile Type',
          notBeforeOffset: 'Not Before Offset',
          ocspServiceLocator: 'OCSP Service Locator',
        },
      },
      profileForm: {
        createTitle: 'Create Profile',
        editTitle: 'Edit Profile',
        maxCertificateValidity: 'Max Certificate Validity*',
        overridableExtensionOIDsTitle: 'Overridable Extension OIDs',
        removeOverridableExtensionOIDs: 'Remove extension',
        addOverridableExtensionOIDs: 'Add Overridable Extension OIDs',
        confirmCancel: 'Do you want to discard your changes?',
        buttons: {
          cancel: 'Cancel',
          update: 'Update',
          create: 'Create',
        },
        disabledCaTooltip:
          'Certification Authority is only required by the End Entity type',
        inputs: {
          [ProfileFormField.profileName]: {
            label: 'Profile Name',
            placeholder: 'Enter Profile Name',
            errors: {
              required: 'Profile Name is required',
              pattern: 'Invalid Profile Name',
            },
          },
          [ProfileFormField.profileType]: {
            label: 'Profile Type',
            placeholder: 'Choose Profile Type',
            options: [
              {
                value: ProfileType.END_ENTITY,
                label: profileTypeEnUs[ProfileType.END_ENTITY],
              },
              {
                value: ProfileType.SUB_CA,
                label: profileTypeEnUs[ProfileType.SUB_CA],
              },
              {
                value: ProfileType.ROOT_CA,
                label: profileTypeEnUs[ProfileType.ROOT_CA],
              },
            ],
            errors: {
              required: 'Profile Type is required',
            },
          },
          [ProfileFormField.maxCertificateValidityDays]: {
            label: 'Days',
            placeholder: 'Enter Days',
            errors: {
              min: 'The value must not be negative',
              maxCertValidityRequired:
                'Please specify max certificate validity',
            },
          },
          [ProfileFormField.maxCertificateValidityMonths]: {
            label: 'Months',
            placeholder: 'Enter Months',
            errors: {
              min: 'The value must not be negative',
              maxCertValidityRequired:
                'Please specify max certificate validity',
              allZero: 'At least 1 validity inputs must be non 0!',
            },
          },
          [ProfileFormField.maxCertificateValidityYears]: {
            label: 'Years',
            placeholder: 'Enter Years',
            errors: {
              min: 'The value must not be negative',
              maxCertValidityRequired:
                'Please specify max certificate validity',
            },
          },
          [ProfileFormField.overridableExtensionOIDs]: {
            label: 'Overridable Extension OIDs',
            placeholder: 'Enter Overridable Extension',
            errors: {
              duplicate: 'Duplicate value',
            },
          },
          [ProfileFormField.allowExtensionOverride]: {
            label: 'Allow Extension Override',
          },
          [ProfileFormField.criticalBasicConstraints]: {
            label: 'Critical Basic Constraints',
          },
          [ProfileFormField.crlDistributionEndpoint]: {
            label: 'CRL Distribution Endpoint',
            placeholder: 'Enter CRL distribution endpoint',
            errors: {
              pattern: 'The value must be a valid URL',
            },
          },
          [ProfileFormField.criticalCRLDistributionPoint]: {
            label: 'Critical CRL Distribution Point',
          },
          [ProfileFormField.useLastToFirstDnOrder]: {
            label: 'Use Last To First DN Order',
          },
          [ProfileFormField.ca]: {
            label: 'Certification Authority',
            placeholder: 'Choose Certification Authority',
            errors: {
              required:
                'Certification Authority is required for End Entity profile type',
            },
          },
          [ProfileFormField.pathLengthConstraint]: {
            label: 'Path Length Constraint',
            placeholder: 'Enter Path Length Constraint',
            errors: {
              min: 'Path Length Constraint must not be negative',
            },
          },
          [ProfileFormField.ocspServiceLocator]: {
            label: 'OCSP Service Locator',
            placeholder: 'Enter OCSP Service Locator',
            errors: {
              pattern: 'Invalid OCSP Service Locator',
            },
          },
        },
      },
      cryptotokenlist: {
        title: 'Crypto Token List',
        createButton: 'Create',
        headers: {
          tokenName: 'Token Name',
          active: 'Active',
          autoActivation: 'Auto-activation',
        },
        actions: {
          activate: {
            tooltip: 'Activate',
            dialogConfig: {
              title: 'Crypto Token password',
              label: 'Password',
              placeholder: 'Enter password',
            },
            defaultErrorMessage: 'Could not activate token',
          },
          deactivate: {
            tooltip: 'Deactivate',
            confirmation: 'Do you want to deactivate this token?',
            defaultErrorMessage: 'Could not deactivate token',
          },
          delete: {
            tooltip: 'Delete token',
            confirmation: (tokenName: string) =>
              `Do you want to delete the token ${tokenName}?`,
            defaultErrorMessage: 'Could not delete token',
          },
        },
        details: {
          title: (tokenName: string) => `${tokenName}`,
          na: 'n/a',
          back: 'Back to Crypto Token List',
          properties: {
            tokenName: 'Name',
            tokenType: 'Type',
            autoActivation: 'Auto-activation',
            active: 'Active',
          },
          cryptoTokenKeys: {
            keydetails: {
              alias: 'Alias',
              keyAlgorithm: 'Key Algorithm',
              keySpecification: 'Key Specification',
              subjectKeyID: 'Subject Key ID',
              createdTime: 'Created Time',
              updateTime: 'Update Time',
            },
            actions: {
              create: {
                title: 'Create Key',
                confirmCancel: 'Do you want to discard your changes?',
                copyTitle: 'Copy Key',
                inputs: {
                  [CreateCryptoTokenKeyField.alias]: {
                    label: 'Alias',
                    placeholder: 'Enter alias',
                    errors: {
                      required: 'Alias field is required!',
                      pattern: 'Invalid alias!',
                    },
                  },
                  [CreateCryptoTokenKeyField.keySpecification]: {
                    label: 'Key Specification',
                    placeholder: 'Select Key Specification',
                    errors: {
                      required: 'Key Specification is required!',
                    },
                    options: KeySpecificationList,
                  },
                },
              },
            },
            buttons: {
              generate: 'Generate new key pair',
            },
            toasts: {
              created: 'Successfully created key',
              fail: 'Failed to create key',
            },
          },
          actions: {
            delete: {
              confirmation: (keyAlias: string) =>
                `Do you want to delete the key ${keyAlias}?`,
              tooltip: 'Delete key',
            },
            copy: {
              tooltip: 'Copy key',
            },
          },
        },
        toasts: {
          fetchingError: 'Error fetching Crypto Token List',
        },
      },
      profileData: {
        toasts: {
          fetchingError: 'Error fetching profiles',
          defaultDeletionError: (profile: Profile) =>
            `Could not delete profile ${profile.profileName}`,
          deletionSuccess: (profile: Profile) =>
            `Profile ${profile.profileName} successfully deleted`,
        },
      },
      cryptotokenData: {
        toasts: {
          fetchingError: 'Error fetching crypto tokens',
          defaultDeletionError: (ct: CryptoToken) =>
            `Could not delete crypto token ${ct.tokenName}`,
          deletionSuccess: (ct: CryptoToken) =>
            `Token ${ct.tokenName} successfully deleted`,
        },
      },
      cryptotokenKeys: {
        toasts: {
          fetchingError: (tokenName: string) =>
            `Error fetching keys for token ${tokenName}`,
          defaultDeletionError: (ctKey: CryptotokenKey) =>
            `Could not delete key ${ctKey.details.alias} for token ${ctKey.tokenName}`,
          deletionSuccess: (ctKey: CryptotokenKey) =>
            `Key ${ctKey.details.alias} for token ${ctKey.tokenName} successfully deleted`,
        },
      },
      provisionOperationalCa: {
        title: 'Provision Operational CA',
        confirmCancel: 'Do you want to discard your changes?',
        buttons: {
          create: 'Create',
          cancel: 'Cancel',
        },
        inputs: {
          [CaFullFormField.caType]: {
            label: 'CA Type',
            placeholder: 'Select CA Type',
            options: [
              { value: CaType.ICA, label: 'ICA' },
              { value: CaType.Root, label: 'Root' },
            ],
            errors: {
              required: 'Please select CA type',
            },
          },
          [CaFullFormField.cryptoTokenType]: {
            label: 'Crypto Token Type',
            placeholder: 'Select Crypto Token Type',
            options: CryptoTokenTypeList,
            errors: {
              required: 'Crypto Token Type is required',
            },
          },
          [CaFullFormField.cryptoTokenName]: {
            label: 'Crypto Token Name',
            placeholder: 'Enter Crypto Token Name',
            errors: {
              required: 'Crypto Token Name is required',
              // FIXME add validator
              pattern: 'Invalid Token Name',
            },
          },
          [CaFullFormField.dn]: {
            label: 'DN',
            placeholder: 'Enter DN',
            errors: {
              required: 'Password is required',
            },
          },
          [CaFullFormField.signedBy]: {
            label: 'Signed By',
            placeholder: 'Select signing CA',
            errors: {
              required: 'Signed By is required',
            },
          },
          [CaFullFormField.keySpecification]: {
            label: 'Key Specification',
            placeholder: 'Select Key Specification',
            options: KeySpecificationList,
            errors: {
              required: 'Key Specification is required',
            },
          },
          [CaFullFormField.validityYears]: {
            label: 'Validity (years)',
            placeholder: 'Select Validity',
            errors: {
              required: 'Validity is required',
            },
          },
          [CaFullFormField.certificateMaxValidityYears]: {
            label: 'Certificate Max Validity (years)',
            placeholder: 'Select Certificate Max Validity',
            errors: {
              required: 'Certificate Max Validity is required',
              certificateMaxValidityYearsExceedsValidityYears:
                'Certificate Max Validity must not exceed Validity',
            },
          },
          [CaFullFormField.profileName]: {
            label: 'Profile Name',
            placeholder: 'Enter Profile Name',
            errors: {
              pattern: 'Invalid Profile Name',
            },
          },
          [CaFullFormField.caCertificateProfile]: {
            label: 'CA Certificate Profile',
            placeholder: 'Select CA Certificate Profile',
            errors: {
              required: 'CA Certificate Profile is required',
            },
          },
          [CaFullFormField.password]: {
            label: 'Password',
            placeholder: 'Enter Password',
            errors: {
              required: 'Password is required',
            },
          },
          [CaFullFormField.useLdapDnOrder]: {
            label: 'Use LDAP DN Order',
          },
          [CaFullFormField.signingAlgo]: {
            label: 'Signing Algorithm',
            placeholder: 'Choose Signing Algorithm',
            errors: {
              required: () => 'Signing Algorithm is required',
            },
          },
        },
        disabledReasons: {
          signedByForNotICA: 'Signed By is only needed for ICA CA',
          certificateMaxValidityForNotICA:
            'Certificate Max Validity is only needed for ICA CA',
          onlySoftTokensForRootCaRequirePassword:
            'Only SOFT crypto tokens for ROOT CA require password',
          tokenExists: (tokenName: string) =>
            `Token ${tokenName} exists and does not require password`,
        },
        defaultError: 'Error provisioning operational CA',
        successMessage: 'Successfully created CA',
      },
    },
    shared: {
      countries,
      usaStates,
      yesNo: {
        [YesNo.Yes]: 'Yes',
        [YesNo.No]: 'No',
      },
      dateWithShortMonthName: (date: Date) => {
        const day = date.getDate();
        const month = date.getMonth();
        const year = date.getFullYear();
        const monthName = SHORT_MONTHS_EN[month];
        return `${monthName} ${day}, ${year}`;
      },
      clipboard: {
        successMessage: 'Successfully Copied',
        tooltip: 'Copy to clipboard',
      },
      confirmDialog: {
        title: 'Confirm',
        okText: 'OK',
        cancelText: 'Cancel',
      },
      inputDialog: {
        okText: 'OK',
        cancelText: 'Cancel',
      },
      okText: 'OK',
      cancelText: 'Cancel',
      total: {
        totalText: (total: number) => `Total: ${total}`,
      },
      table: {
        pageSizeLabel: 'Page Size:',
        createPageMessage: (currentPage: number, totalPages: number | null) => {
          if (totalPages === null) {
            return `Page ${currentPage}`;
          }
          return `Page ${currentPage} of ${totalPages}`;
        },
        createRecordMessage: (
          fromRecord: number,
          toRecord: number,
          totalRecords: number | null,
        ) => {
          if (totalRecords === null) {
            return `${fromRecord} to ${toRecord}`;
          }
          return `${fromRecord} to ${toRecord} of ${totalRecords}`;
        },
        noRowsOverlay: {
          defaultNoResultsMessage: 'There are no results',
          defaultRetryMessage: '(Try another search)',
        },
      },
      extendedKeyUsageTitle: 'Extended Key Usage',
      extendedKeyUsages: {
        [ExtendedKeyUsages.csn369791TlsClient]: 'CSN 369791 TLS client',
        [ExtendedKeyUsages.csn369791TlsServer]: 'CSN 369791 TLS server',
        [ExtendedKeyUsages.clientAuthentication]: 'Client Authentication',
        [ExtendedKeyUsages.codeSigning]: 'Code Signing',
        [ExtendedKeyUsages.eapOverLan]: 'EAP over LAN (EAPOL)',
        [ExtendedKeyUsages.eapOverPpp]: 'EAP over PPP',
        [ExtendedKeyUsages.etsiTslSigning]: 'ETSI TSL Signing',
        [ExtendedKeyUsages.emailProtection]: 'Email Protection',
        [ExtendedKeyUsages.icaoDeviationListSigning]:
          'ICAO Deviation List Signing',
        [ExtendedKeyUsages.icaoMasterListSigning]: 'ICAO Master List Signing',
        [ExtendedKeyUsages.intelAmtManagement]: 'Intel AMT management',
        [ExtendedKeyUsages.internetKeyExchangeForIpsec]:
          'Internet Key Exchange for IPsec',
        [ExtendedKeyUsages.kerberosClientAuthentication]:
          'Kerberos Client Authentication',
        [ExtendedKeyUsages.kerberosKdc]:
          'Kerberos KDC (Key Distribution Center)',
        [ExtendedKeyUsages.msCommercialCodeSigning]:
          'MS Commercial Code Signing',
        [ExtendedKeyUsages.msDocumentSigning]: 'MS Document Signing',
        [ExtendedKeyUsages.msEfsRecovery]: 'MS EFS Recovery',
        [ExtendedKeyUsages.msEncryptedFileSystem]:
          'MS Encrypted File System (EFS)',
        [ExtendedKeyUsages.msIndividualCodeSigning]:
          'MS Individual Code Signing',
        [ExtendedKeyUsages.msSmartCardLogon]: 'MS Smart Card Logon',
        [ExtendedKeyUsages.ocspSigner]: 'OCSP Signer',
        [ExtendedKeyUsages.pdfSigning]: 'PDF Signing',
        [ExtendedKeyUsages.pivCardAuthentication]: 'PIV Card Authentication',
        [ExtendedKeyUsages.scvpClient]: 'SCVP Client',
        [ExtendedKeyUsages.scvpServer]: 'SCVP Server',
        [ExtendedKeyUsages.sipDomain]: 'SIP Domain',
        [ExtendedKeyUsages.sshClient]: 'SSH Client',
        [ExtendedKeyUsages.sshServer]: 'SSH Server',
        [ExtendedKeyUsages.serverAuthentication]: 'Server Authentication',
        [ExtendedKeyUsages.timeStamping]: 'Time Stamping',
      } as Record<string, string>,
      keyUsageTitle: 'Key Usage',
      criticalKeyUsageLabel: 'Critical',
      keyUsages: {
        [KeyUsages.DIGITAL_SIGNATURE]: 'Digital Signature',
        [KeyUsages.NON_REPUDIATION]: 'Non-Repudiation',
        [KeyUsages.KEY_ENCIPHERMENT]: 'Key Encipherment',
        [KeyUsages.DATA_ENCIPHERMENT]: 'Data Encipherment',
        [KeyUsages.KEY_AGREEMENT]: 'Key Agreement',
        [KeyUsages.KEY_CERT_SIGN]: 'Key Certificate Sign',
        [KeyUsages.CRL_SIGN]: 'CRL Sign',
        [KeyUsages.ENCIPHER_ONLY]: 'Encipher Only',
        [KeyUsages.DECIPHER_ONLY]: 'Decipher Only',
      },
      profileType: profileTypeEnUs,
      profileTypeOther: 'Other',
      profileTypeUnknown: 'Unknown',
      standardInputs: {
        select: {
          none: 'NONE',
        },
        country: {
          label: 'Country',
          placeholder: 'Enter country',
          errors: {
            invalidCompletion: () => 'Invalid country',
          },
          options: countries.map(({ name }) => ({
            value: name,
          })),
        },
        usState: {
          label: 'State',
          placeholder: 'Enter state',
          errors: {
            invalidCompletion: () => 'Invalid state',
          },
          options: usaStates.map(({ name }) => ({
            value: name,
          })),
        },
      },
      fetchingErrors: {
        calist: 'Error fetching Certification Authorities',
        cryptotokenSlots: 'Error fetching cryptotoken slots',
      },
    },
    newCryptotoken: {
      title: 'New Crypto Token',
      inputs: {
        secretName: {
          label: 'Secret Name',
          placeholder: 'Enter Secret Name',
          errors: {
            required: 'Secret Name is required for an auto-activated token',
          },
        },
        password: {
          label: 'Password',
          placeholder: 'Enter Password',
          errors: {
            required: 'Password is required for a non-auto-activated token',
          },
        },
        [CryptoTokenField.tokenType]: {
          label: 'Token Type',
          placeholder: 'Enter Token Type',
          options: CryptoTokenTypeList,
          errors: {
            required: () => 'Token Type is required',
          },
        },
        [CryptoTokenField.tokenName]: {
          label: 'Token Name',
          placeholder: 'Enter Token Name',
          errors: {
            required: 'Token Name is required',
            pattern: 'Invalid Token Name',
          },
        },
        [CryptoTokenField.autoActivation]: {
          label: 'Auto-activation',
        },
        [CryptoTokenField.slotLabel]: {
          label: 'Slot Label',
          options: [],
        },
      },
      cancelWarning: 'Do you want to discard the changes?',
      buttons: {
        ok: 'OK',
        cancel: 'Cancel',
      },
      toasts: {
        created: 'Successfully created crypto token',
        fail: 'Failed to create crypto token',
      },
    },
  },
};

export type Langs = typeof LANG;
export type Lang = Langs['en-US'];
export type LangLocale = keyof Langs;
